export const supportedFileTypes = ["doc", "docx", "pdf", "msg", "xlsx", "png", "jpeg", "jpg"];

// Configurable character limits
export const COMMENTS_MAX_LENGTH = 1000;

const errorMessages = {
  browser: {
    protocolSetupForm: {
      lpo: {
        required: "LPO is required."
      },
      protocolNumber: {
        required: "Protocol is required.",
        helpText: "If you cannot find a protocol, please contact the CTSU Help Desk."
      },
      formPartType: {
        required: "Please select a setup form."
      },
      validation: {
        error: "Error validating form. Please try again.",
        duplicate: (setupType, statusLabel) =>
          `This protocol already has a ${setupType} request in ${statusLabel} status.`,
        invalidTooltip: "Please complete required fields to continue.",
        validTooltip: "Initiate the new protocol request.",
        ancillaryDuplicate: "Ancillary study has duplicate records.",
        irocDuplicate: "IROC credentialing has duplicate records.",
        stepDuplicate: "Step information has duplicate records.",
        psrDuplicate: "PSR has duplicate records."
      },
      labels: {
        openRaveSetup: "OPEN/Rave setup",
        regulatorySetup: "Regulatory setup"
      }
    }
  },
  common: {
    fileUpload: {
      allowedFormats: `Only ${supportedFileTypes.map(ext => `.${ext}`).join(", ")} files are allowed`
    },
    comments: {
      max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`,
      required: "Comments are required."
    },
    openRave: {
      max1000: "Cannot exceed 1000 characters."
    }
  },
  openRave: {
    infoForm: {
      requestType: {
        required: "Request Type is required.",
        min: "Request Type is required."
      },
      listOfChanges: {
        required: "At least one change must be selected.",
        min: "At least one change must be selected."
      },
      describeOtherChanges: {
        required: "Describe other changes is required.",
        max: "Cannot exceed 500 characters."
      },
      revisionEligibilityCriteria: {
        max: "Cannot exceed 500 characters."
      },
      protocolType: {
        required: "Protocol Type is required.",
        min: "Protocol Type is required."
      },
      indType: {
        required: "IND Type is required.",
        min: "IND Type is required."
      },
      indAgent: {
        max: "Cannot exceed 100 characters.",
        required: "IND Agent is required."
      },
      indHolder: {
        max: "Cannot exceed 100 characters.",
        required: "IND Holder is required."
      },
      indNumber: {
        max: "Cannot exceed 100 characters.",
        required: "IND Number is required."
      },
      tentativeActivationDate: {
        required: "Activation date is required.",
        min: "Activation date is required."
      },
      openContact: {
        required: "At least one OPEN Contact must be selected.",
        min: "At least one OPEN Contact must be selected."
      },
      raveContact: {
        required: "At least one RAVE Contact must be selected.",
        min: "At least one RAVE Contact must be selected."
      },
      otherEmailContacts: {
        test: "Each email must be valid and separated by comma."
      }
    },
    raveSetupForm: {
      isRaveProtocol: {
        required: "Is this a Rave protocol is required."
      },
      raveInstanceUrl: {
        required: "Rave instance name is required."
      },
      isUatStudyNameAsProd: {
        required: "Select yes or no."
      },
      uatRaveStudyName: {
        max: "Cannot exceed 100 characters.",
        required: "UAT Rave study name is required."
      },
      openRaveAlsVersion: {
        required: "ALS version is required."
      },
      isRaveCtepAersInt: {
        required: "Select yes or no."
      },
      ctepAersStepNumber: {
        typeError: "Must be a number.",
        min: "Must be at least 1.",
        max: "Cannot exceed 2 digits.",
        required: "CTEP-AERS initial step is required."
      },
      isTsdvSiteAuditing: {
        required: "Select yes or no."
      },
      isDqpSupported: {
        required: "Select yes or no."
      },
      isStudyRaveCalendaring: {
        required: "Select yes or no."
      },
      isSdpSupported: {
        required: "Select yes or no."
      },
      cmEffectiveDt: {
        required: "Effective date is required."
      },
      cmStepNumber: {
        typeError: "Must be a number.",
        min: "Must be at least 1.",
        max: "Cannot exceed 2 digits.",
        required: "Step is required."
      },
      cmPatientSelectionMethod: {
        required: "Selection method is required."
      },
      patientNumberSchema: {
        typeError: "Must be a number.",
        max: "Cannot exceed 4 digits.",
        required: (label) => `${label} is required.`
      },
      isMedidataPatientCloudEpro: {
        required: "Select yes or no."
      },
      isRequiredTriadTesting: {
        required: "Select yes or no."
      }
    },
    openSetupForm: {
      ancillaryStudy: {
        groupProtocolNumber: {
          max: "Cannot exceed 100 characters.",
          required: "Group Protocol is required."
        },
        stepNumber: {
          typeError: "Must be a number.",
          min: "Step number must be at least 1.",
          max: "Cannot exceed 2 digits.",
          required: "Step is required."
        },
        mandatory: {
          required: "Select an option."
        }
      },
      irocDetails: {
        type: {
          max: "Cannot exceed 100 characters.",
          required: "Type is required."
        },
        requirement: {
          required: "Select an option."
        }
      },
      step: {
        required: {
          required: "Select yes or no."
        },
        publicFormIds: {
          max: "Cannot exceed 100 characters.",
          required: "Field is required."
        },
        crfVersionNum: {
          max: "Cannot exceed 100 characters.",
          required: "Field is required."
        },
        stepNumber: {
          max: "Cannot exceed 2 characters.",
          required: "Field is required."
        },
        stepDescription: {
          max: "Cannot exceed 100 characters.",
          required: "Field is required."
        },
        boolSelect: {
          required: "Select yes or no."
        }
      },
      isStandardRandoNodeUrl: {
        required: "Select yes or no."
      },
      randoNodeUrl: {
        max: "Cannot exceed 200 characters.",
        required: "RandoNode URL is required.",
        url: "Enter a valid URL."
      },
      isEmbeddedAncillaryStudy: {
        required: "Select yes or no."
      },
      isSlotReservationProtocol: {
        required: "Select yes or no."
      },
      slotReservationSteps: {
        required: "Step is required.",
        test: "Each step must be max 2 digits, separated by comma."
      },
      isIrocCredentialingInfoOpen: {
        required: "Select yes or no."
      },
      requestedTestSites: {
        max: "Cannot exceed 100 characters.",
        required: "Requested test sites is required."
      },
      additionalSetupNotes: {
        max: "Cannot exceed 1000 characters."
      }
    },
    pendingSignOffForm: {
      groupAdmin: {
        releaseDateTime: "Release Date & Time is required.",
        groupAdminSignOff: "Group Admin sign off must be confirmed.",
        groupAdminComments: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      },
      openAdmin: {
        openAdminSignOff: "Open Admin sign off must be confirmed.",
        openAdminComments: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      },
      irocAdmin: {
        irocAdminSignOff: "IROC Admin sign off must be confirmed.",
        irocAdminComments: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      }
    },
    groupTestingForm: {
      comments: {
        max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`,
        required: "Comments are required."
      }
    },
    openRaveSubmitForm: {
      comments: {
        max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      },
      duplicateCtsuUser: "The same CTSU User is selected for Primary and Secondary OPEN Admin. Please select two different users.",
      primaryComments: {
        max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      },
      secondaryComments: {
        max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      }
    },
    ctsuTestingForm: {
      comments: {
        max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`,
        required: "Comments are required."
      }
    },
    completedForm: {
      comments: {
        max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      }
    }
  },
  regulatory: {
    regulatorySetupForm: {
      protocolVersionDt: {
        required: "Protocol version date is required."
      },
      protocolVersionNumber: {
        max: "Cannot exceed 100 characters.",
        required: "Protocol version is required."
      },
      levelIrbReviewRequired: {
        required: "Level of IRB review is required."
      },
      ohrpCategory: {
        required: "OHRP category is required."
      },
      whenExpeditedReviewAllowed: {
        required: "When is expedited review allowed is required."
      },
      releaseDt: {
        required: "Release Date & Time is required."
      },
      ctsuCollectionRequired: {
        boolean: "Select yes or no.",
        required: "CTSU Collection Required is required."
      },
      anticipatedPrimaryCompletionDt: {
        required: "Anticipated Primary Completion Date is required."
      },
      isCollectIrbApprovalPostClosure: {
        boolean: "Select yes or no.",
        required: "Collect IRB approval post-closure is required."
      },
      countries: {
        required: "Participating countries is required.",
        min: "Participating countries is required."
      },
      isCtaRequired: {
        boolean: "Select yes or no."
      },
      ctaRequiredInfo: {
        max: "Cannot exceed 100 characters.",
        required: "CTA Holder is required."
      },
      participationChangeDesc: {
        max: "Cannot exceed 500 characters."
      },
      indAgent: {
        max: "Cannot exceed 100 characters.",
        required: "IND agent is required."
      },
      indHolder: {
        required: "IND holder is required."
      },
      indNumber: {
        max: "Cannot exceed 100 characters.",
        required: "IND number is required."
      },
      psrDetails: {
        required: "At least one PSR Requirement is required.",
        min: "At least one PSR Requirement is required."
      },
      attachPSR: {
        test: "Only .doc, .pdf, .msg, .xlsx files are allowed."
      }
    },
    infoForm: {
      requestType: {
        required: "Request Type is required."
      },
      primaryContact: {
        min: "Primary Contact is required."
      }
    },
    psrForm: {
      psrName: {
        required: "Name is required.",
        max: "Cannot exceed 100 characters."
      },
      psrVersionDt: {
        required: "For Version Date is required."
      },
      psrType: {
        required: "PSR Type is required."
      },
      psrRequiredByDt: {
        required: "Required By Date is required.",
        future: "Date must be today or in the future."
      },
      isEvaluationByGroup: {
        required: "Evaluation by Group is required."
      },
      requestedStatus: {
        required: "Status is required."
      },
      sourceSubmittedBy: {
        required: "Source is required."
      },
      ctsuProcessingInstructions: {
        max: "Cannot exceed 4000 characters.",
        required: "CTSU Processing Instructions is required."
      },
      psrComments: {
        max: "Cannot exceed 500 characters."
      },
      psrContactName: {
        max: "Cannot exceed 100 characters.",
        required: "Name is required."
      },
      psrContactPhoneNum: {
        max: "Cannot exceed 100 characters.",
        required: "Phone is required."
      },
      psrContactEmailAddress: {
        max: "Cannot exceed 100 characters.",
        required: "Email is required."
      },
      psrDistributionList: {
        max: "Cannot exceed 500 characters."
      }
    },
    signOff: {
      acknowledgmentRequired: "Acknowledgment is required.",
      submitForm: {
        groupAdminComments: {
          max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
        }
      },
      inProgress: {
        regComments: {
          max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`,
          required: "Comments are required."
        },
        isRegAckCompletedYn: {
          required: "Acknowledgment is required."
        }
      },
      additional: {
        irocAdminComments: {
          max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
        }
      }
    }
  }
};

export const titleAndAria = {
  statusRetired: 'Retired',
  statusCanceled: 'Canceled',
  statusCompleted: 'Completed',
  updateNotAllowed: (title, status) => `Update to ${title} is not allowed when request status is ${status}.`,
  expandCollapse: (isOpen, title) => `${isOpen ? "Collapse" : "Expand"} ${title} Section`,
  cannotExpandCollapse: (title, status) => `${title} section cannot be expanded or collapsed. \nEditing is not allowed when request status is ${status}.`,
  viewPsr: "View PSR Requirement",
  editPsr: "Edit PSR Requirement",
  removePsr: "Remove PSR from request",
  makesChangesExistingPsr: "Makes change(s) to an existing PSR",
  addPsr: "Add PSR",
  newPsr: "New PSR",
  addNewProtocolSetupRequest: "Add new protocol setup request",
  openSupplementalChecklist: "Open the Supplemental Checklist in a new tab",
  viewRequest: (requestNumber) => `View Request ${requestNumber}`,
  clearFilters: "clear filters",
  viewRequestHistory: (requestNumber) => `View Request ${requestNumber} history`,
  submitRequest: "Submit Request",
  closeNotification: "Close notification",
  clearChanges: "Clear changes",
  noChangesToClear: "No changes to clear",
  save: "Save",
  saveRequest: "Save request",
  addStudy: "Add Study",
  deleteRow: "Delete this row",
  deleteRowConfirmation: "Are you sure you want to delete this row?",
  addType: "Add Type",
  addStep: "Add Step",
  sendReminder: "Send Reminder",
  saveLabel: (saveLabel, formTitle) => `${saveLabel} ${formTitle || ''}`,
  undoTitle: (title) => `Undo ${title}`,
  viewLatestPart: (formName, requestNumber) => `View latest ${formName} part for Request ${requestNumber} in new tab`,
  attachmentRedactionWarning: (label) => `${label} (attachments should be redacted, remove PII)`,
  delete: "Delete",
  addNewRow: "Add New Row"
};

export default errorMessages;
